import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import type { ReactNode } from 'react';
import { AuthProvider, useAuth } from './state/auth.js';
import { CartProvider } from './state/cart.js';
import { Layout } from './components/Layout.js';
import { Catalog } from './pages/Catalog.js';
import { ProductDetail } from './pages/ProductDetail.js';
import { Checkout } from './pages/Checkout.js';
import { OrderConfirmation } from './pages/OrderConfirmation.js';
import { Orders } from './pages/Orders.js';
import { OrderDetail } from './pages/OrderDetail.js';
import { Login } from './pages/Login.js';
import { Register } from './pages/Register.js';
import { Account } from './pages/Account.js';
import { NotFound } from './pages/NotFound.js';
import { AdminLayout } from './pages/admin/AdminLayout.js';
import { Dashboard } from './pages/admin/Dashboard.js';
import { AdminProducts } from './pages/admin/AdminProducts.js';
import { AdminOrders } from './pages/admin/AdminOrders.js';
import { Skeleton } from './components/ui.js';

/**
 * Route table.
 *
 * `AuthProvider` wraps `CartProvider` because the cart reloads when the identity
 * changes — signing in merges a guest basket into the member's basket, and the
 * client needs to know that happened.
 *
 * Guards render a skeleton while the session is being restored rather than
 * redirecting immediately. Redirecting on `status === 'loading'` would bounce
 * every signed-in user to the sign-in page on a hard refresh, because the
 * refresh call has not answered yet.
 */
export function App() {
  return (
    <AuthProvider>
      <CartProvider>
        <Routes>
          <Route element={<Layout />}>
            <Route index element={<Catalog />} />
            <Route path="product/:slug" element={<ProductDetail />} />
            <Route path="checkout" element={<Checkout />} />
            <Route path="checkout/confirmation/:orderId" element={<OrderConfirmation />} />
            <Route
              path="orders"
              element={
                <RequireAuth>
                  <Orders />
                </RequireAuth>
              }
            />
            <Route
              path="orders/:id"
              element={
                <RequireAuth>
                  <OrderDetail />
                </RequireAuth>
              }
            />
            <Route
              path="account"
              element={
                <RequireAuth>
                  <Account />
                </RequireAuth>
              }
            />
            <Route path="login" element={<Login />} />
            <Route path="register" element={<Register />} />
            <Route
              path="admin"
              element={
                <RequireStaff>
                  <AdminLayout />
                </RequireStaff>
              }
            >
              <Route index element={<Dashboard />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="orders" element={<AdminOrders />} />
            </Route>
            <Route path="*" element={<NotFound />} />
          </Route>
        </Routes>
      </CartProvider>
    </AuthProvider>
  );
}

function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <RouteFallback />;
  if (status === 'anonymous') {
    // `state.from` lets the sign-in page send the user back where they were
    // heading. Without it, clicking "Orders" while signed out lands you on the
    // catalogue after signing in, which feels like the app ignored you.
    return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  }
  return <>{children}</>;
}

function RequireStaff({ children }: { children: ReactNode }) {
  const { status, isStaff } = useAuth();

  if (status === 'loading') return <RouteFallback />;
  if (status === 'anonymous') return <Navigate to="/login" replace state={{ from: '/admin' }} />;
  // A signed-in customer who guesses the admin URL gets the 404 page, not a
  // "you do not have permission" message. The distinction matters: a 403
  // confirms the route exists.
  if (!isStaff) return <NotFound />;
  return <>{children}</>;
}

function RouteFallback() {
  return (
    <div className="mx-auto max-w-7xl space-y-4 px-4 py-16 sm:px-6 lg:px-8">
      <Skeleton className="h-8 w-64" />
      <Skeleton className="h-4 w-full max-w-xl" />
      <Skeleton className="h-64 w-full" />
    </div>
  );
}
